import { useState, useCallback } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import * as actions from '~/store/actions';
import { MessageTypeEnum, ChannelTypeEnum } from '~/enums/conversations';
import { DraftMessage } from '~/interfaces/conversations';
import { _ } from '~/dependencies';
import { log, electron } from '~/helpers';
import { resolveChannelType } from '~/helpers/conversation';
import api from '~/services';

export enum SMSErrorCode {
  SMS_INSUFFICIENT_CREDIT = 6001,
  SMS_NO_VALID_RECIPIENTS = 6002,
  SMS_INVALID_SENDER_ID = 6003,
  SMS_IRISH_COMREG_RESTRICTED = 6004,
  SMS_USA_CAMPAIGN_NOT_APPROVED = 6005,
  SMS_DELIVERY_FAILED = 6006,
  SMS_SCHEDULED_MESSAGE_ERROR = 6007,
  SMS_BULK_SEND_PARTIAL_FAILURE = 6008,
  SMS_BULK_SEND_COMPLETE_FAILURE = 6009,
  SMS_DRAFT_MESSAGE_NOT_FOUND = 6010,
  SMS_INSUFFICIENT_LIMIT = 6011,
  SMS_ERROR = 400,
}

export enum LaravelErrorCodeEnum {
  INVALID_DATA = 42222,
}

export enum ErrorCodeEnum {
  UNKNOWN = 60000,
}

/**
 * The API is retunning the errors in different formats. This function normalizes
 * the error object to a standard structure.
 */
export function getErrorCode(error: any) {
  if (typeof error !== 'object') return ErrorCodeEnum.UNKNOWN;

  if ('data' in error) {
    if ('errorCode' in error.data) {
      return error.errorCode;
    }

    // Laravel validation errors
    if ('errors' in error.data) {
      return LaravelErrorCodeEnum.INVALID_DATA;
    }
  }

  return ErrorCodeEnum.UNKNOWN;
}

/**
 * Classifies the message type based on the presence of a template ID or the file type.
 * Defaults to TEXT if no specific type is identified.
 */
export function classifyMessageType({
  templateId,
  file,
}: DraftMessage): MessageTypeEnum {
  if (templateId) return MessageTypeEnum.TEXT;

  if (file) {
    if (file.type.startsWith('image/')) return MessageTypeEnum.IMAGE;
    if (file.type.startsWith('video/')) return MessageTypeEnum.VIDEO;
    if (file.type.startsWith('audio/')) return MessageTypeEnum.AUDIO;
    return MessageTypeEnum.FILE;
  }

  return MessageTypeEnum.TEXT;
}

export function isFileMessageType(messageType: MessageTypeEnum): boolean {
  const messageTypes = [
    MessageTypeEnum.IMAGE,
    MessageTypeEnum.VIDEO,
    MessageTypeEnum.AUDIO,
    MessageTypeEnum.FILE,
  ];

  return messageTypes.includes(messageType);
}

function buildMetadata(draft: DraftMessage): Record<string, unknown> {
  return {
    ...(_.isNumber(draft.templateId) && { template_id: draft.templateId }),
    ...(!_.isEmpty(draft.templateParams) && {
      template_variables: draft.templateParams,
    }),
  };
}

/**
 *  WhatsApp only supports content text or a file attachment but not both together.
 *  WhatsApp sends files via FormData; this guards that branch in buildWhatsAppPayload.
 */
function composeWhatsAppPayload(
  draft: DraftMessage,
  messageType: MessageTypeEnum
): FormData {
  const payload = new FormData();
  payload.append('channel_type', String(ChannelTypeEnum.WHATSAPP));
  if (isFileMessageType(messageType)) {
    payload.append('content_file', draft.file!);
  } else {
    payload.append('content', draft.message);
  }
  payload.append('message_type', String(messageType));

  if (_.isNumber(draft.templateId)) {
    payload.append('metadata[template_id]', String(draft.templateId));
  }
  Object.entries(draft.templateParams || {}).forEach(([key, value]) => {
    payload.append(`metadata[template_variables][${key}]`, String(value));
  });

  return payload;
}

/**
 * SMS only supports text content text.
 * MessageType is always TEXT for SMS, even if a template is used.
 */
function composeSMSPayload(draft: DraftMessage): Record<string, unknown> {
  const metadata = buildMetadata(draft);
  return {
    content: draft.message,
    message_type: MessageTypeEnum.TEXT,
    channel_type: ChannelTypeEnum.SMS,
    ...(!_.isEmpty(metadata) && { metadata }),
  };
}

function composeMMSPayload(draft: DraftMessage): Record<string, unknown> {
  const metadata = buildMetadata(draft);
  return {
    ...(draft.message && { content: draft.message }),
    channel_type: ChannelTypeEnum.MMS,
    ...(!_.isEmpty(metadata) && { metadata }),
    ...(draft.mediaIds?.length && { media_ids: draft.mediaIds }),
  };
}

function handleAddMessageError(
  err: unknown,
  targetId: string,
  draft: DraftMessage,
  resolvedChannelType: ChannelTypeEnum
): string {
  const errorMessage =
    (err as any)?.isAxiosError && (err as any)?.response?.data?.message
      ? (err as any).response?.data?.message
      : getErrorMessageByCode(getErrorCode(err));

  // Don't log message or contacts which contain personal info to main.log, but log them to web log for debugging.
  const formatted = `NUACOM-LOG: [CONVERSATIONS] - Failed to add message to conversation ID ${targetId}. Reason: ${errorMessage} | Raw Error: ${JSON.stringify(
    err
  )} | Payload: ${JSON.stringify({
    channelType: resolvedChannelType,
    templateId: draft.templateId,
    templateParams: draft.templateParams,
    messageType: draft.file?.type,
  })}`;

  log.error(formatted, { message: draft.message });
  electron.log(formatted);

  return errorMessage;
}

type AgentInfo = {
  userId: number;
  userName: string;
  accountId?: number;
};

function composeWhatsAppInitialPayload(
  draft: DraftMessage,
  agentInfo: AgentInfo
) {
  const {
    channelType,
    contacts: rawContacts,
    groups,
    senderId,
    templateId,
    templateParams,
  } = draft;
  const { userId, userName } = agentInfo;

  const contacts = rawContacts || '';
  const templateVariables = templateParams || {};

  const senderParticipant = {
    participant_type: 'user',
    participant_id: userId,
    display_name: userName,
    is_system_user: 1,
  };

  const contactParticipants = contacts
    .split(',')
    .map(contact => contact.trim())
    .filter(contact => !_.isEmpty(contact))
    .map(contact => ({
      participant_type: 'phone_number',
      participant_id: contact,
      display_name: contact,
    }));

  return {
    channel_type: channelType,
    sender_id: senderId,
    participants: [senderParticipant, ...contactParticipants],
    ...(_.isString(groups) && !_.isEmpty(groups) && { groups }),
    message: {
      message_type: MessageTypeEnum.TEXT,
      metadata: {
        ...(_.isNumber(templateId) && { template_id: templateId }),
        ...(!_.isEmpty(templateVariables) && {
          template_variables: templateVariables,
        }),
      },
    },
  };
}

function composeSmsInitialPayload(draft: DraftMessage, agentInfo: AgentInfo) {
  const {
    channelType,
    contacts: rawContacts,
    groups,
    senderId,
    senderName,
    templateId,
    message,
  } = draft;
  const { userId, accountId } = agentInfo;

  const contacts = rawContacts || '';

  return {
    channel_type: channelType,
    title: senderName || null,
    sender_id: senderId || null,
    is_group: false,
    owner_id: userId,
    account_id: accountId,
    ...(_.isString(contacts) && !_.isEmpty(contacts) && { contacts }),
    ...(_.isString(groups) && !_.isEmpty(groups) && { groups }),
    message: {
      content: message,
      message_type: 0,
      ...(_.isNumber(templateId) && { metadata: { template_id: templateId } }),
    },
  };
}

function composeMmsInitialPayload(draft: DraftMessage, agentInfo: AgentInfo) {
  const {
    contacts: rawContacts,
    groups,
    templateId,
    message,
    mediaIds,
    senderId,
  } = draft;
  const { userId, accountId } = agentInfo;

  const contacts = rawContacts || '';

  return {
    sender_id: senderId || null,
    channel_type: ChannelTypeEnum.MMS,
    owner_id: userId,
    account_id: accountId,
    ...(_.isString(contacts) && !_.isEmpty(contacts) && { contacts }),
    ...(_.isString(groups) && !_.isEmpty(groups) && { groups }),
    message: {
      content: message,
      ...(_.isNumber(templateId) && { metadata: { template_id: templateId } }),
      ...(mediaIds?.length && { media_ids: mediaIds }),
    },
  };
}

/**
 * Maps SMS error codes to user-friendly messages.
 */
export function getErrorMessageByCode(
  errorCode: number = ErrorCodeEnum.UNKNOWN
): string {
  switch (errorCode) {
    case SMSErrorCode.SMS_INSUFFICIENT_CREDIT:
      return "You don't have enough credit to send this message.";
    case SMSErrorCode.SMS_NO_VALID_RECIPIENTS:
      return 'No valid recipients were found for this message.';
    case SMSErrorCode.SMS_INVALID_SENDER_ID:
      return 'The sender ID is invalid. Please verify and try again.';
    case SMSErrorCode.SMS_IRISH_COMREG_RESTRICTED:
      return 'Message sending is restricted by Irish ComReg regulations.';
    case SMSErrorCode.SMS_USA_CAMPAIGN_NOT_APPROVED:
      return 'The SMS campaign has not been approved for the USA region.';
    case SMSErrorCode.SMS_DELIVERY_FAILED:
      return 'Message delivery failed. Please try again later.';
    case SMSErrorCode.SMS_SCHEDULED_MESSAGE_ERROR:
      return 'There was an issue with the scheduled message.';
    case SMSErrorCode.SMS_BULK_SEND_PARTIAL_FAILURE:
      return 'Some messages failed to send in this bulk operation.';
    case SMSErrorCode.SMS_BULK_SEND_COMPLETE_FAILURE:
      return 'All messages failed to send. Please review your recipients.';
    case SMSErrorCode.SMS_DRAFT_MESSAGE_NOT_FOUND:
      return 'The draft message could not be found.';
    case SMSErrorCode.SMS_INSUFFICIENT_LIMIT:
      return "You've reached your SMS limit. Please upgrade or wait for renewal.";
    case SMSErrorCode.SMS_ERROR:
      return 'No valid numbers to send to.';
    case LaravelErrorCodeEnum.INVALID_DATA:
      return 'The given data was invalid.';
    default:
      return "We're sorry, something went wrong. Please try again shortly.";
  }
}

export default function useConversation() {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const {
    account_id: accountId,
    userIdCode: userId,
    user_name: userName,
  } = useSelector((state: RootStateOrAny) => state.auth);

  /**
   * Create a new conversation and returns conversationID
   */
  const create = useCallback(
    async (draft: DraftMessage) => {
      try {
        if (!accountId || !userId) {
          throw new Error('User authentication details are missing.');
        }

        const { channelType: rawChannelType, mediaIds } = draft;
        const channelType = resolveChannelType(rawChannelType, mediaIds);

        setLoading(true);

        let payload: FormData | Record<string, unknown>;

        switch (channelType) {
          case ChannelTypeEnum.WHATSAPP:
            // WhatsApp has a distinct payload structure. We need to use the `participants` array instead of `contacts`.
            payload = composeWhatsAppInitialPayload(draft, {
              userId,
              userName,
            });
            break;
          case ChannelTypeEnum.MMS:
            payload = composeMmsInitialPayload(draft, {
              userId,
              userName,
              accountId,
            });
            break;
          case ChannelTypeEnum.SMS:
            payload = composeSmsInitialPayload(draft, {
              userId,
              userName,
              accountId,
            });
            break;
          default:
            throw new Error(`Unsupported channel type: ${channelType}`);
        }

        /* eslint-disable no-unreachable */
        // return;

        const response = await api.conversation.post.createConversation(
          payload
        );

        if (response.data?.id) {
          setConversationId(response.data.id);

          // Refresh conversation details to reflect the new message after 10 seconds.
          setTimeout(() => {
            dispatch(actions.fetchConversationById(response.data?.id, false));
          }, 10000); // 10 seconds
        }
      } catch (err) {
        const errorCode = getErrorMessageByCode(getErrorCode(err));
        setErrorMessage(errorCode);

        // Dont now log message or contacts which contains personal info to main.log, but log them to web log for debugging.
        const errorNewMessageFormatted = `NUACOM-LOG: [CONVERSATIONS] - Failed to start new conversation. Reason: ${errorCode} | Raw Error: ${JSON.stringify(
          err
        )} | Payload: ${JSON.stringify(draft)}`;

        log.error(errorNewMessageFormatted);
        electron.log(errorNewMessageFormatted);
      } finally {
        setLoading(false);
        setSubmitted(true);
      }
    },
    [
      setLoading,
      setSubmitted,
      setConversationId,
      accountId,
      userId,
      dispatch,
      userName,
    ]
  );

  /**
   * Add message to existing conversation using conversationID
   */
  const addMessage = useCallback(
    async (draft: DraftMessage) => {
      const targetId = draft.conversationId!;
      const channelType = resolveChannelType(draft.channelType, draft.mediaIds);

      log.debug('Raw conversation payload: ', {
        ...draft,
        channelType,
      });

      try {
        if (!targetId || !channelType) {
          throw new Error('Conversation ID or Channel Type is missing.');
        }

        setLoading(true);

        const messageType = classifyMessageType(draft);

        let payload: FormData | Record<string, unknown>;

        switch (channelType) {
          case ChannelTypeEnum.WHATSAPP:
            payload = composeWhatsAppPayload(draft, messageType);
            break;
          case ChannelTypeEnum.MMS:
            payload = composeMMSPayload(draft);
            break;
          case ChannelTypeEnum.SMS:
            payload = composeSMSPayload(draft);
            break;
          default:
            throw new Error(`Unsupported channel type: ${channelType}`);
        }

        log.debug('Payload for adding message: ', payload);

        // /* eslint-disable no-unreachable */
        // return;

        await api.conversation.post.addMessageToConversation(targetId, payload);

        // Immediately refresh conversation details to reflect the new message.
        setTimeout(() => {
          dispatch(actions.fetchConversationById(targetId, false));
        }, 1000); // 1 second

        // Retry to refresh conversation details to reflect the new message after 10 seconds.
        setTimeout(() => {
          dispatch(actions.fetchConversationById(targetId, false));
        }, 10000); // 10 seconds
      } catch (err) {
        setErrorMessage(
          handleAddMessageError(err, targetId, draft, channelType)
        );
      } finally {
        setLoading(false);
      }
    },
    [setErrorMessage, setLoading, dispatch]
  );

  return {
    errorMessage,
    loading,
    create,
    addMessage,
    setErrorMessage,
    submitted,
    conversationId,
    setSubmitted,
  };
}
